import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, FileText, Calendar, Plus, ClipboardList, Receipt,
  KeyRound, X, AlertCircle, Eye, EyeOff, Package, Building2, BookOpen,
} from 'lucide-react';

// Páginas que se reutilizan como contenido de cada pestaña
import ClientePedidos from './ClientePedidos';
import ClienteFacturacion from './ClienteFacturacion';
import CatalogoProductos from './CatalogoProductos';

const PRIMARY = '#0099FF';
const PRIMARY_DARK = '#0077CC';
const PRIMARY_LIGHT = 'rgba(0, 153, 255, 0.08)';
const PRIMARY_BORDER = 'rgba(0, 153, 255, 0.25)';

const formatDate = (dateStr) => {
  if (!dateStr) return 'Sin pedidos';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/* ─── Modal de cambio de contraseña ──────────────────────────────── */
const ChangePasswordModal = ({ isOpen, onClose, token, API_URL }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleClose = () => {
    setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    setError(''); setSuccess(''); setShowCurrent(false); setShowNew(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (newPassword.length < 6) { setError('La nueva contraseña debe tener al menos 6 caracteres'); return; }
    if (newPassword !== confirmPassword) { setError('Las contraseñas no coinciden'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/auth/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al cambiar la contraseña');
      setSuccess(data.message || 'Contraseña actualizada correctamente');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4 relative">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <KeyRound size={20} style={{ color: PRIMARY }} /> Cambiar Contraseña
          </h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md"><AlertCircle size={16} /><span className="text-sm">{error}</span></div>}
          {success && <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-md text-sm">{success}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña actual</label>
            <div className="relative">
              <input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña</label>
            <div className="relative">
              <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showNew ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar nueva contraseña</label>
            <input type={showNew ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={handleClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm text-white rounded-md disabled:opacity-50" style={{ backgroundColor: PRIMARY }}>{saving ? 'Guardando...' : 'Cambiar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Componente principal ───────────────────────────────────────── */
const ClienteHome = () => {
  const { token, API_URL, user, activeClient, setActiveClient, clientHierarchy, loadHierarchy } = useAuth();
  const navigate = useNavigate();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [stats, setStats] = useState(null);
  const [vistaActiva, setVistaActiva] = useState('mis-pedidos');

  // Recargar jerarquía al montar (por si entró directo a esta ruta)
  useEffect(() => {
    if (user?.permisos === 5 && (!clientHierarchy || clientHierarchy.length === 0)) {
      loadHierarchy(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // KPIs minimizadas: una sola línea con números pequeños
  useEffect(() => {
    if (!user) return;
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/dashboard/client-stats`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) setStats(await res.json());
      } catch { /* silencioso */ }
    };
    fetchStats();
  }, [user, API_URL, token, activeClient]);

  // Helpers para sidebar: agrupar por matriz si hay >1
  const hierarchy = clientHierarchy || [];
  const hasSidebar = hierarchy.length > 1;
  const activeId = activeClient?.id || null;

  return (
    <div className="h-full flex bg-gray-50">

      {/* ─── SIDEBAR LATERAL (solo si tiene >1 clínica) ─── */}
      {hasSidebar && (
        <aside className="w-72 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
          <div className="px-4 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Building2 size={18} style={{ color: PRIMARY }} /> Mis Clínicas
            </h2>
            <p className="text-xs text-gray-500 mt-1">{hierarchy.length} centros disponibles</p>
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {hierarchy.map((c) => {
              const isActive = activeId === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveClient(c)}
                  className={`w-full text-left px-4 py-3 transition-colors border-l-4 ${
                    isActive
                      ? 'border-l-blue-500 bg-blue-50'
                      : 'border-l-transparent hover:bg-gray-50'
                  }`}
                  style={isActive ? { borderLeftColor: PRIMARY, backgroundColor: PRIMARY_LIGHT } : undefined}
                >
                  <div className={`text-sm font-medium truncate ${isActive ? 'text-blue-700' : 'text-gray-800'}`}
                       style={isActive ? { color: PRIMARY_DARK } : undefined}>
                    {c.razon_social}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>
      )}

      {/* ─── ÁREA PRINCIPAL ─── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Header compacto: bienvenida + métricas pequeñas en línea + acciones */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-lg font-bold text-gray-800">
                  {activeClient?.razon_social || stats?.clienteNombre || `Bienvenido, ${user?.nombrec || user?.username}`}
                </h1>
                <p className="text-xs text-gray-500">
                  {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                {activeClient && (activeClient.calle || activeClient.localidad) && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    <span className="text-gray-400">Entrega:</span>{' '}
                    {[
                      activeClient.calle,
                      [activeClient.c_postal, activeClient.localidad].filter(Boolean).join(' '),
                      activeClient.provincia
                    ].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
              {/* Métricas pequeñas inline (no protagonistas) */}
              <div className="hidden md:flex items-center gap-4 ml-6 pl-6 border-l border-gray-200 text-xs">
                <div className="flex items-center gap-1.5 text-gray-600">
                  <ShoppingCart size={14} className="text-amber-500" />
                  <span><b className="text-gray-800">{stats?.pedidosPendientes ?? 0}</b> pendientes</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-600">
                  <FileText size={14} className="text-blue-500" />
                  <span><b className="text-gray-800">{stats?.pedidosTotales ?? 0}</b> totales</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Calendar size={14} className="text-green-500" />
                  <span>Último: <b className="text-gray-800">{formatDate(stats?.ultimoPedido)}</b></span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowChangePassword(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-gray-600 hover:bg-gray-100"
                title="Cambiar contraseña"
              >
                <KeyRound size={14} />
                <span className="hidden sm:inline">Contraseña</span>
              </button>
            </div>
          </div>
        </div>

        {/* ─── PESTAÑAS ─── */}
        <div className="bg-white border-b border-gray-200 px-6 flex-shrink-0">
          <nav className="flex gap-1 -mb-px overflow-x-auto">
            {[
              { id: 'mis-pedidos', label: 'Mis Pedidos', icon: ClipboardList },
              { id: 'nuevo-pedido', label: 'Nuevo Pedido', icon: Plus },
              { id: 'facturacion', label: 'Facturación', icon: Receipt },
              { id: 'catalogo', label: 'Catálogo', icon: BookOpen },
            ].map((tab) => {
              const isActive = vistaActiva === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === 'nuevo-pedido') {
                      navigate('/cliente/nuevo-pedido');
                    } else {
                      setVistaActiva(tab.id);
                    }
                  }}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                  style={isActive ? { borderColor: PRIMARY, color: PRIMARY } : undefined}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* ─── CONTENIDO DE LA PESTAÑA ACTIVA ─── */}
        <div className="flex-1 overflow-auto">
          {vistaActiva === 'mis-pedidos' && <ClientePedidos embedded />}
          {vistaActiva === 'facturacion' && <ClienteFacturacion embedded />}
          {vistaActiva === 'catalogo' && <CatalogoProductos embedded />}
        </div>
      </div>

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        token={token}
        API_URL={API_URL}
      />
    </div>
  );
};

export default ClienteHome;
